import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api, API } from "@/lib/api";
import { CATEGORY_COLOR } from "@/data/site";
import { Seo } from "@/components/Seo";
import { ArrowLeft } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div className="App bg-ink">
      {post && (
        <Seo
          title={`${post.title} | DNA eCommerce Ads Blog`}
          description={post.meta_description || post.excerpt}
          image={`${API}/og/blog/${post.slug}.png`}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.meta_description || post.excerpt,
            image: [`${API}/og/blog/${post.slug}.png`, post.cover_image].filter(Boolean),
            articleSection: post.category,
            datePublished: post.created_at,
            dateModified: post.updated_at || post.created_at,
            author: { "@type": "Organization", name: post.author || "DNA eCommerce Ads" },
            publisher: {
              "@type": "Organization",
              name: "DNA eCommerce Ads",
              logo: { "@type": "ImageObject", url: `${window.location.origin}/dna-logo.png` },
            },
            mainEntityOfPage: window.location.href,
          }}
        />
      )}
      <Header />
      <main className="mx-auto max-w-3xl px-5 pb-24 pt-36 lg:pt-44" data-testid="blog-post-page">
        <button onClick={() => navigate("/blog")} className="mb-8 inline-flex items-center gap-2 text-sm text-muted hover:text-white" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4" /> All articles
        </button>

        {notFound ? (
          <div className="py-20 text-center">
            <h1 className="font-display text-3xl font-bold">Article not found</h1>
            <Link to="/blog" className="mt-4 inline-block text-orange">Back to the blog</Link>
          </div>
        ) : !post ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <article>
            <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${CATEGORY_COLOR[post.category] || "text-muted border-white/20"}`}>
              {post.category}
            </span>
            <h1 className="mt-6 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl" data-testid="post-title">{post.title}</h1>
            <div className="mt-5 flex items-center gap-3 text-sm text-muted">
              <span>{post.author}</span>
              <span>·</span>
              <span>{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="mt-8 aspect-[16/9] w-full rounded-2xl object-cover" />
            )}
            <div className="prose-article mt-10">
              {/<[a-z][\s\S]*>/i.test(post.content) ? (
                <div dangerouslySetInnerHTML={{ __html: post.content }} />
              ) : (
                post.content.split("\n").filter(Boolean).map((para, i) => <p key={i}>{para}</p>)
              )}
            </div>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
